'use client';
import { Session } from 'next-auth';
import { useSession } from 'next-auth/react';
import { useEffect } from 'react';

import { getAnalyticsIdentity } from '@/actions/posthog/analytics';
import { useAnalytics } from '@/context/AnalyticsContext';
import { Analytics } from '@/utils/analytics';

let lastIdentity: string | null = null;

export const identifyAnalyticsUser = async (
    analytics: Analytics,
    user: Session['user'],
    isCancelled: () => boolean = () => false
) => {
    const identifyAs = user.trackingId as string;
    const email = user.email ?? null;
    if (user.id === undefined) return;

    const res = await getAnalyticsIdentity();
    if (isCancelled() || res.status !== 'success') return;
    const { subscription, teamId, teamName } = res.data;

    const identityKey = JSON.stringify({ identifyAs, email, subscription, teamId });
    const mismatch = analytics.getDistinctId() !== identifyAs;
    if (lastIdentity === identityKey && !mismatch) return;

    if (mismatch && analytics.isIdentified()) {
        analytics.reset();
    }

    analytics.identify(identifyAs, {
        email,
        subscriptionName: subscription?.type ?? null,
        subscription,
        teamName,
    });
    if (teamId) analytics.group('company', teamId.toString(), { name: teamName ?? undefined });
    analytics.register({ subscription });

    lastIdentity = identityKey;
};

export const AnalyticsAuthObserver: React.FC = () => {
    const analytics = useAnalytics();
    const { data: session, status } = useSession();

    useEffect(() => {
        if (status === 'loading') return;

        if (status !== 'authenticated' || !session?.user) {
            if (lastIdentity !== null || analytics.isIdentified()) {
                analytics.reset();
                lastIdentity = null;
            }
            return;
        }

        let cancelled = false;
        void identifyAnalyticsUser(analytics, session.user, () => cancelled);

        return () => {
            cancelled = true;
        };
    }, [status, session?.user, analytics]);

    return null;
};
